import { Box } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { fmtMoneyDebug, fmtIrrPct } from './calc/formatters';

function formatSummaryValue(value, format) {
  if (format === 'raw') {
    return value != null && value !== '' ? String(value) : '—';
  }
  if (value == null || !Number.isFinite(Number(value))) {
    return '—';
  }
  if (format === 'money') {
    return fmtMoneyDebug(value, 0);
  }
  if (format === 'pct') {
    return fmtIrrPct(Number(value));
  }
  if (format === 'multiple') {
    return `${Number(value).toFixed(2)}x`;
  }
  return String(value);
}

function SummaryValueCell({ value, format = 'money', bold = false, className = '' }) {
  return (
    <td className={`deal-irr-summary-value ${bold ? 'deal-irr-summary-value--bold' : ''} ${className}`}>
      {formatSummaryValue(value, format)}
    </td>
  );
}

SummaryValueCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  format: PropTypes.string,
  bold: PropTypes.bool,
  className: PropTypes.string,
};

function yearTotalFee(yearRow) {
  return (Number(yearRow?.financingFeeCash) || 0) + (Number(yearRow?.facilityFeeCash) || 0);
}

function buildSummaryRows(summary) {
  const years = summary.years || [];
  const yearKeys = years.map((yearRow) => yearRow.year);

  const rows = [
    { type: 'title', label: null },
    {
      type: 'metric',
      label: 'Max exposure (estimated)',
      value: summary.maxExposure,
      format: 'money',
    },
    {
      type: 'metric',
      label: 'Commitment (mos.)',
      value: summary.commitmentMonths,
      format: 'raw',
    },
    {
      type: 'metric',
      label: 'Max facility size (headline origination)',
      value: summary.headlineOrigination,
      format: 'money',
    },
    { type: 'spacer' },
    { type: 'section', label: 'Credit Return' },
    {
      type: 'creditHeader',
      cells: ['Fee', 'Rate', 'Calculation basis', 'IRR'],
    },
    {
      type: 'credit',
      label: 'Financing fee',
      rate: summary.financingFeeRate,
      basis: 'Monthly exposure',
      irr: summary.financingIrrAnnual,
    },
    {
      type: 'credit',
      label: 'Facility fee',
      rate: summary.facilityFeeRate,
      basis: 'Total headline origination number',
      irr: summary.facilityIrrAnnual,
    },
    {
      type: 'creditTotal',
      label: 'Total',
      irr: summary.creditTotalIrr,
    },
    { type: 'spacer' },
  ];

  if (years.length > 0) {
    rows.push({ type: 'yearHeader', years: yearKeys });
  }

  rows.push(
    {
      type: 'metric',
      label: 'Cash at risk (max exposure est.)',
      value: summary.cashAtRiskMax,
      format: 'money',
      yearKey: 'cashAtRiskMax',
    },
    {
      type: 'metric',
      label: 'Net cash return',
      value: summary.netCashReturn,
      format: 'money',
    },
    {
      type: 'metric',
      label: 'Cash return on max exposure',
      value: summary.cashReturnOnMax,
      format: 'multiple',
    },
    { type: 'spacer' },
    {
      type: 'metric',
      label: 'Cash at risk (Avg exposure est.)',
      value: summary.cashAtRiskAvg,
      format: 'money',
      yearKey: 'cashAtRiskAvg',
    },
    {
      type: 'metric',
      label: 'Net cash return',
      value: summary.netCashReturn,
      format: 'money',
    },
    {
      type: 'metric',
      label: 'Cash return on Avg exposure est.',
      value: summary.cashReturnOnAvg,
      format: 'multiple',
    },
    { type: 'spacer' },
    {
      type: 'metric',
      label: 'Total Growth Investment',
      value: summary.outflow,
      format: 'money',
      yearKey: 'outflow',
    },
    {
      type: 'metric',
      label: 'Total Returns',
      value: summary.inflow,
      format: 'money',
      yearKey: 'inflow',
    },
    {
      type: 'metric',
      label: 'Net cash',
      value: summary.netCash,
      format: 'money',
      yearKey: 'netCash',
      separator: true,
    },
    {
      type: 'metric',
      label: 'Cash-on-cash',
      value: summary.cashOnCash,
      format: 'multiple',
    },
    { type: 'spacer' },
    {
      type: 'metric',
      label: 'Fee',
      value: summary.financingFeeCash,
      format: 'money',
      yearKey: 'financingFeeCash',
    },
    {
      type: 'metric',
      label: 'Facility Fee',
      value: summary.facilityFeeCash,
      format: 'money',
      yearKey: 'facilityFeeCash',
    },
    {
      type: 'metric',
      label: 'Total Fee',
      value: summary.totalFeeCash,
      format: 'money',
      yearKey: 'totalFee',
      separator: true,
    },
    {
      type: 'metric',
      label: 'Total Fee/ Avg exposure est.',
      value: summary.feeOnAvg,
      format: 'multiple',
    },
    {
      type: 'metric',
      label: 'Total Fee/ max exposure est',
      value: summary.feeOnMax,
      format: 'multiple',
    }
  );

  return { rows, years };
}

function DealIrrSummarySection({ summary, title }) {
  if (!summary) {
    return null;
  }

  const { rows, years } = buildSummaryRows(summary);
  const dataColCount = Math.max(3, years.length > 0 ? 1 + years.length : 3);
  const totalCols = 1 + dataColCount;

  return (
    <Box className="card deal-irr-summary-card" mt={6}>
      <Box overflowX="auto">
        <table className="deal-irr-summary-table">
          <tbody>
            {rows.map((row, index) => {
              if (row.type === 'title') {
                return (
                  <tr key={`row-${index}`} className="deal-irr-summary-title-row">
                    <td colSpan={totalCols}>{title}</td>
                  </tr>
                );
              }

              if (row.type === 'spacer') {
                return (
                  <tr key={`row-${index}`} className="deal-irr-summary-spacer-row">
                    <td colSpan={totalCols} />
                  </tr>
                );
              }

              if (row.type === 'section') {
                return (
                  <tr key={`row-${index}`} className="deal-irr-summary-section-row">
                    <td colSpan={totalCols}>{row.label}</td>
                  </tr>
                );
              }

              if (row.type === 'creditHeader') {
                return (
                  <tr key={`row-${index}`} className="deal-irr-summary-header-row">
                    <td className="deal-irr-summary-label">{row.cells[0]}</td>
                    <td className="deal-irr-summary-value deal-irr-summary-value--bold">
                      {row.cells[1]}
                    </td>
                    <td className="deal-irr-summary-value deal-irr-summary-value--bold">
                      {row.cells[2]}
                    </td>
                    <td className="deal-irr-summary-value deal-irr-summary-value--bold">
                      {row.cells[3]}
                    </td>
                    {dataColCount > 3
                      ? Array.from({ length: dataColCount - 3 }, (_, colIndex) => (
                          <td key={`pad-${colIndex}`} />
                        ))
                      : null}
                  </tr>
                );
              }

              if (row.type === 'credit') {
                return (
                  <tr key={`row-${index}`} className="deal-irr-summary-data-row">
                    <td className="deal-irr-summary-label">{row.label}</td>
                    <SummaryValueCell value={row.rate} format="pct" />
                    <td className="deal-irr-summary-basis">{row.basis}</td>
                    <SummaryValueCell value={row.irr} format="pct" />
                    {dataColCount > 3
                      ? Array.from({ length: dataColCount - 3 }, (_, colIndex) => (
                          <td key={`pad-${colIndex}`} />
                        ))
                      : null}
                  </tr>
                );
              }

              if (row.type === 'creditTotal') {
                return (
                  <tr key={`row-${index}`} className="deal-irr-summary-data-row">
                    <td className="deal-irr-summary-label deal-irr-summary-label--bold">
                      {row.label}
                    </td>
                    <td />
                    <td />
                    <SummaryValueCell value={row.irr} format="pct" bold />
                    {dataColCount > 3
                      ? Array.from({ length: dataColCount - 3 }, (_, colIndex) => (
                          <td key={`pad-${colIndex}`} />
                        ))
                      : null}
                  </tr>
                );
              }

              if (row.type === 'yearHeader') {
                return (
                  <tr key={`row-${index}`} className="deal-irr-summary-year-header-row">
                    <td className="deal-irr-summary-label" />
                    <td />
                    {row.years.map((year) => (
                      <td key={year} className="deal-irr-summary-year-label">
                        {year}
                      </td>
                    ))}
                    {dataColCount > 1 + row.years.length
                      ? Array.from(
                          { length: dataColCount - 1 - row.years.length },
                          (_, colIndex) => <td key={`pad-${colIndex}`} />
                        )
                      : null}
                  </tr>
                );
              }

              if (row.type === 'metric') {
                return (
                  <tr
                    key={`row-${index}`}
                    className={`deal-irr-summary-data-row${
                      row.separator ? ' deal-irr-summary-data-row--separator' : ''
                    }`}
                  >
                    <td className="deal-irr-summary-label">{row.label}</td>
                    <SummaryValueCell value={row.value} format={row.format} />
                    {years.map((yearRow) =>
                      row.yearKey ? (
                        <SummaryValueCell
                          key={`${row.label}-${yearRow.year}`}
                          value={
                            row.yearKey === 'totalFee'
                              ? yearTotalFee(yearRow)
                              : yearRow[row.yearKey]
                          }
                          format="money"
                        />
                      ) : (
                        <td key={`${row.label}-${yearRow.year}`} />
                      )
                    )}
                    {dataColCount > 1 + years.length
                      ? Array.from(
                          { length: dataColCount - 1 - years.length },
                          (_, colIndex) => <td key={`pad-${colIndex}`} />
                        )
                      : null}
                  </tr>
                );
              }

              return null;
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

DealIrrSummarySection.propTypes = {
  summary: PropTypes.object,
  title: PropTypes.string,
};

export { buildSummaryRows, formatSummaryValue };
export default DealIrrSummarySection;
